import { getObjectDiff } from '@hourly/utilities';
import {
  Alert,
  AlertVariant,
  Button,
  ButtonSize,
  ButtonVariant,
  Dialog,
  EmailInput,
  MaskedInput,
  Size,
  TextInput,
} from '@hourly-hds/components';
import { Smartphone } from '@hourly-hds/icons/glyph';
import { useState } from 'react';
import { z } from 'zod';
import { User } from '../../data/models/users.model';
import styles from './EditUserDialog.module.css';

const userSchema = z.object({
  firstName: z.string().min(1, 'Please enter first name.'),
  lastName: z.string().min(1, 'Please enter last name.'),
  email: z.email('Please enter a valid email.'),
  mobileNumber: z.string().regex(/^[1-9]\d{9}$/, 'Please enter a valid mobile number.'),
});

type UserForm = z.infer<typeof userSchema>;

const initialUserForm: UserForm = {
  firstName: '',
  lastName: '',
  email: '',
  mobileNumber: '',
};

const EditUserDialog = ({
  currentUser,
  onClose,
  onFinish,
  errorMessage,
}: {
  currentUser: User;
  onClose: () => void;
  onFinish: (user: UserForm & { id: string }) => void;
  errorMessage?: string;
}) => {
  const [errors, setErrors] = useState<Partial<Record<keyof UserForm, string>>>({});
  const [user, setUser] = useState<UserForm>({ ...initialUserForm, ...currentUser });

  const handleChangeUser = (changes: Partial<UserForm>) => {
    setUser((currentState) => ({ ...currentState, ...changes }));
  };

  const handleClose = () => {
    setUser(initialUserForm);
    setErrors({});
    onClose();
  };

  const diff = getObjectDiff(currentUser, user);
  const canSave = Object.keys(diff).length > 0;

  const hasErrorMessage = !!errorMessage;

  const handleSave = () => {
    const result = userSchema.safeParse(user);
    if (!result.success) {
      const tree = z.treeifyError(result.error);
      const properties = tree.properties ?? {};

      setErrors({
        firstName: properties?.firstName?.errors[0],
        lastName: properties?.lastName?.errors[0],
        email: properties?.email?.errors[0],
        mobileNumber: properties?.mobileNumber?.errors[0],
      });

      return;
    }

    onFinish({ ...currentUser, ...user });
  };

  return (
    <div className={styles.overlayWrapper}>
      <Dialog
        open={!!currentUser}
        size={Size.Large}
        onClose={handleClose}
        aria-label="Edit User Dialog">
        <Dialog.Header>
          <div className={hasErrorMessage ? styles.headerWithAlert : styles.header}>
            <span className={styles.title}>Edit User</span>
          </div>
        </Dialog.Header>
        <Dialog.Content>
          {hasErrorMessage && (
            <div className={styles.alert}>
              <Alert variant={AlertVariant.Error}>
                <Alert.Header>{errorMessage}</Alert.Header>
              </Alert>
            </div>
          )}
          <div className={styles.form}>
            <div className={styles.twoCols}>
              <div className={styles.field}>
                <TextInput
                  label="First Name"
                  value={user.firstName}
                  onChange={(firstName) => handleChangeUser({ firstName })}
                  error={!!errors.firstName}
                  description={errors.firstName}
                />
              </div>
              <div className={styles.field}>
                <TextInput
                  label="Last Name"
                  value={user.lastName}
                  onChange={(lastName) => handleChangeUser({ lastName })}
                  error={!!errors.lastName}
                  description={errors.lastName}
                />
              </div>
            </div>

            <div className={styles.field}>
              <EmailInput
                label="Email"
                value={user.email}
                onChange={(email) => handleChangeUser({ email })}
                error={!!errors.email}
                description={errors.email}
              />
            </div>

            <div className={styles.field}>
              <MaskedInput
                label="Mobile Number"
                value={user.mobileNumber}
                onChange={(mobileNumber) => handleChangeUser({ mobileNumber })}
                mask="(000) 000 0000"
                icon={Smartphone}
                error={!!errors.mobileNumber}
                description={errors.mobileNumber}
              />
            </div>
          </div>
        </Dialog.Content>
        <Dialog.Footer>
          <Button label="Cancel" onClick={handleClose} fullWidth size={ButtonSize.Large} />
          <Button
            label="Save"
            disabled={!canSave}
            onClick={handleSave}
            variant={ButtonVariant.Primary}
            size={ButtonSize.Large}
            fullWidth
          />
        </Dialog.Footer>
      </Dialog>
    </div>
  );
};

export default EditUserDialog;
